import { z } from 'zod';
/** The Newswire satellite's desks, in the order the board shows them. */
export const NEWS_DESKS = [
    'tech',
    'geopolitics',
    'birmingham',
    'indonesia',
    'middle_east',
    'viral',
];
export const newsDeskSchema = z.enum(NEWS_DESKS);
export const NEWS_DESK_LABELS = {
    tech: 'Tech & security',
    geopolitics: 'Geopolitics',
    birmingham: 'Birmingham',
    indonesia: 'Indonesia · Jember',
    middle_east: 'Middle East',
    viral: 'World-viral',
};
export const newsStateSchema = z.enum(['fresh', 'interested', 'following', 'dismissed']);
export const newsSourceSchema = z.object({
    url: z.string(),
    outlet: z.string(),
    title: z.string(),
    publishedAt: z.string().nullable(),
});
export const newsItemSchema = z.object({
    id: z.string().uuid(),
    desk: newsDeskSchema,
    headline: z.string(),
    dek: z.string(),
    whyItMatters: z.string(),
    importance: z.number(),
    /** Learned rank: importance shifted by the taste profile. */
    score: z.number(),
    /** Below the taste threshold: shown collapsed under "Probably not for you". */
    muted: z.boolean(),
    publishedAt: z.string().nullable(),
    createdAt: z.string(),
    state: newsStateSchema,
    seen: z.boolean(),
    sources: z.array(newsSourceSchema),
    threadId: z.string().uuid().nullable(),
});
export const newsThreadUpdateSchema = z.object({
    id: z.string().uuid(),
    /** The followed story in the group that found it. */
    threadId: z.string().uuid(),
    headline: z.string(),
    body: z.string(),
    publishedAt: z.string().nullable(),
    createdAt: z.string(),
    seen: z.boolean(),
    sources: z.array(newsSourceSchema),
});
export const newsThreadSchema = z.object({
    id: z.string().uuid(),
    itemId: z.string().uuid(),
    title: z.string(),
    brief: z.string(),
    /** The words every new article is matched against (editable). */
    terms: z.array(z.string()),
    active: z.boolean(),
    createdAt: z.string(),
    lastCheckedAt: z.string().nullable(),
    lastUpdateAt: z.string().nullable(),
    lastError: z.string().nullable(),
    unseen: z.number().int(),
    /** Newest first, from every story in the group. */
    updates: z.array(newsThreadUpdateSchema),
    /** The group's own name, when given; shown instead of the head story's title. */
    label: z.string().nullable(),
    /** The other stories grouped under this one, each still watched for by its own words. */
    members: z.array(z.object({ id: z.string().uuid(), itemId: z.string().uuid(), title: z.string(), terms: z.array(z.string()) })),
});
export const newsStatusSchema = z.object({
    unseen: z.number().int(),
    followingUnseen: z.number().int(),
    refreshing: z.boolean(),
    /** Desks still being scanned by the current run, if any. */
    pendingDesks: z.array(newsDeskSchema),
    lastRunAt: z.string().nullable(),
    /** No item has ever been found: the first scan is the one in progress. */
    empty: z.boolean(),
    /** Whether the cheap model may be used (headline wording, thread judging, Explain). */
    ai: z.boolean(),
});
export const newsBoardSchema = z.object({
    items: z.array(newsItemSchema),
    threads: z.array(newsThreadSchema),
    status: newsStatusSchema,
});
export const newsRuleSchema = z.object({
    id: z.string(),
    text: z.string(),
    /** 'more' = wants this, 'less' = doesn't. */
    polarity: z.enum(['more', 'less']),
    /** 'user' = written by the user (a keyword filter). 'learned' is legacy (Claude-written, no longer used). */
    source: z.enum(['learned', 'user']),
});
export const newsTasteSchema = z.object({
    rules: z.array(newsRuleSchema),
    desks: z.record(z.number()),
    /** The strongest learned terms either way, for the "learned" card. */
    topTerms: z.array(z.object({ term: z.string(), weight: z.number() })),
    version: z.number().int(),
    decisions: z.number().int(),
});
export const newsTriageSchema = z.object({
    action: z.enum(['interested', 'following', 'dismissed', 'fresh']),
});
/** A refresh or "check now": whether it started one (false = one is already running). */
export const newsStartedSchema = z.object({ ok: z.literal(true), started: z.boolean() });
/** "Pass on the rest" (or its undo): how many stories moved. */
export const newsBulkResultSchema = z.object({
    ok: z.literal(true),
    moved: z.number().int(),
    /** With all: the stories moved, so they can be put back. */
    ids: z.array(z.string().uuid()).optional(),
});
/** Explain: why a story matters, written once on request and stored with it. */
export const newsExplainSchema = z.object({ whyItMatters: z.string() });
//# sourceMappingURL=news.js.map